import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Copy } from "lucide-react";

export default function MfaSetupDialog({ user, onDone }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("start");
  const [uri, setUri] = useState("");
  const [secret, setSecret] = useState("");
  const [otp, setOtp] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  const start = async () => {
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setUri(data.uri); setSecret(data.secret); setStep("verify");
    } catch { toast.error("Failed to init MFA"); }
  };
  const verify = async () => {
    try {
      const { data } = await api.post("/auth/mfa/verify", { otp });
      toast.success("MFA enabled");
      setRecoveryCodes(data.recovery_codes || []);
      setStep("codes");
      setOtp("");
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
  };
  const closeDialog = () => { setOpen(false); setStep("start"); setOtp(""); setRecoveryCodes(null); };
  const disable = async () => {
    const code = window.prompt("Enter current MFA code to disable:");
    if (!code) return;
    try { await api.post("/auth/mfa/disable", { otp: code }); toast.success("MFA disabled"); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (user?.mfa_enabled) {
    return <Button onClick={disable} variant="outline" size="sm" className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="mfa-disable-btn"><ShieldCheck className="w-3.5 h-3.5 mr-2" /> Disable MFA</Button>;
  }

  const qrSrc = uri ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&bgcolor=111827&color=E5A93C&data=${encodeURIComponent(uri)}` : "";

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setStep("start"); }}>
      <DialogTrigger asChild><Button size="sm" className="btn-gold rounded-full" data-testid="mfa-enable-btn"><ShieldCheck className="w-3.5 h-3.5 mr-2" /> Enable MFA</Button></DialogTrigger>
      <DialogContent className="bg-[#111827] border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display">Two-factor authentication</DialogTitle></DialogHeader>
        {step === "start" ? (
          <div>
            <p className="text-sm text-slate-400">Add a second layer to your admin login using Google Authenticator, 1Password, or Authy.</p>
            <Button onClick={start} className="btn-gold w-full mt-4">Start setup</Button>
          </div>
        ) : step === "verify" ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-400">Scan the QR with your authenticator app, then enter the 6-digit code to confirm.</p>
            {qrSrc && <div className="grid place-items-center"><img src={qrSrc} alt="MFA QR" className="rounded-xl border border-amber-400/30" /></div>}
            <div className="bg-black/40 rounded-lg p-3 flex items-center justify-between text-xs font-mono">
              <span className="text-slate-400 truncate">{secret}</span>
              <button onClick={() => { navigator.clipboard.writeText(secret); toast.success("Copied"); }} className="text-amber-400 hover:text-amber-300"><Copy className="w-3.5 h-3.5" /></button>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-400">6-digit code</Label>
              <Input value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6} className="bg-black/30 border-white/10 h-10 mt-1 font-mono text-center text-lg tracking-widest" data-testid="mfa-verify-otp" />
            </div>
            <Button onClick={verify} className="btn-gold w-full" data-testid="mfa-verify-submit">Verify & enable</Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="mfa-recovery-codes">
            <div className="text-amber-400 text-sm font-bold flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Save these one-time recovery codes</div>
            <p className="text-xs text-slate-400">Store them somewhere safe. Use one if you lose your authenticator. Each code works once.</p>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm">
              {(recoveryCodes || []).map((c, i) => (
                <div key={i} className="bg-black/40 border border-amber-400/20 rounded px-3 py-2 text-amber-300 tracking-wider">{c}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => { navigator.clipboard.writeText((recoveryCodes || []).join("\n")); toast.success("All codes copied"); }} variant="outline" className="bg-transparent border-amber-400/40 text-amber-400 flex-1"><Copy className="w-3.5 h-3.5 mr-2" /> Copy all</Button>
              <Button onClick={closeDialog} className="btn-gold flex-1" data-testid="mfa-codes-done">I've saved them</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
